import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Zap, Target, Crosshair, Calendar, Clock, Users, DollarSign, Award, CheckCircle, Mail } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../context/LanguageContext";
import { usePageContent } from "../hooks/usePageContent";

const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting && !isVisible) setIsVisible(true); }, { threshold: 0.3 });
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [isVisible]);
  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor((1 - Math.pow(1 - progress, 4)) * (end - start) + start));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration, start]);
  return { count, ref };
};

const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold, rootMargin: '50px' });
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);
  return { ref, isVisible };
};

const AnimatedSection = ({ children, className = "", animation = "fly-up", delay = 0 }) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const baseTransform = { 'fly-left': 'translateX(-80px)', 'fly-right': 'translateX(80px)', 'fly-up': 'translateY(60px)', 'fly-down': 'translateY(-60px)', 'zoom': 'scale(0.8)' };
  const style = { opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateX(0) translateY(0) scale(1)' : baseTransform[animation] || 'translateY(60px)', transition: `opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms, transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms` };
  return <div ref={ref} className={className} style={style}>{children}</div>;
};

const AnimatedCounter = ({ end, suffix = "", prefix = "", duration = 2000 }) => {
  const { count, ref } = useCountUp(end, duration);
  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

const PrivateCoachingPage = () => {
  const { lang, t } = useLanguage();
  const { get } = usePageContent("coaching");

  const title = lang === "es" ? "Entrenamiento Privado | Curious 123" : "Private Coaching | Curious 123 Youth Soccer";
  const desc = lang === "es" ? "Lecciones privadas de futbol para edades 8-10. Domina los 123s del juego." : "Private soccer lessons for ages 8-10. Master the 123s of the Beautiful Game with Curious 123.";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href="https://curious123.org/private-coaching" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content="https://curious123.org/private-coaching" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Curious 123" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
      </Helmet>

      <section className="pt-32 pb-20 bg-gradient-to-b from-[#F0F9FF] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-black mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />{t.coaching.backHome}
          </Link>
          <AnimatedSection animation="fly-up" className="max-w-4xl">
            <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">{get("hero_badge", t.coaching.badge)}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-tight mb-6 font-['Kanit']">
              {get("hero_title", t.coaching.heading1)} <span className="text-[#87CEEB]">{get("hero_title") ? "" : t.coaching.heading2}</span>
            </h1>
            <p className="text-lg text-gray-700 max-w-2xl">
              {get("hero_subtitle", t.coaching.subtitle + " " + t.coaching.ages + ".")}
            </p>
            <div className="mt-6 inline-block bg-black text-white px-6 py-3 rounded-full font-semibold animate-pulse">
              {t.coaching.datesPending}
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 bg-[#F0F9FF]" data-testid="program-pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">
              {lang === "es" ? "Detalles del Programa y Precios" : "Program Details & Pricing"}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {lang === "es" ? "Elige el formato de entrenamiento que mejor se adapte a tu joven atleta." : "Choose the training format that best fits your young athlete."}
            </p>
          </AnimatedSection>

          {/* ── Full Team Training (side by side: Tue/Thu Ages 8-10 & Mon/Wed Ages 6-8) ── */}
          <AnimatedSection animation="fly-up">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 font-['Kanit'] mb-1">{lang === "es" ? "Equipo Completo" : "Full Team Training"}</h3>
              <p className="text-sm text-gray-500">{lang === "es" ? "4:00 PM - 5:00 PM | Sesion mensual (4 semanas)" : "4:00 PM - 5:00 PM | Monthly session (4 weeks)"}</p>
            </div>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-5 mb-12" data-testid="full-team-section">
            <AnimatedSection animation="fly-left">
              <div className="bg-white rounded-2xl border-2 border-[#87CEEB] overflow-hidden h-full shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="bg-[#87CEEB] text-black px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    <h3 className="font-bold font-['Kanit']">{lang === "es" ? "Lunes y Miercoles" : "Mondays & Wednesdays"}</h3>
                  </div>
                  <span className="text-xl font-bold font-['Kanit']">$50</span>
                </div>
                <div className="p-5 space-y-2.5">
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{lang === "es" ? "Edades 6 - 8 anos | por 8 sesiones" : "Ages 6 - 8 years old | for 8 sessions"}</p>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Min 8 / Max 15 jugadores" : "Min 8 / Max 15 players"}</p></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Pies rapidos, control, primer toque, duelos 1v1/2v2, juegos reducidos" : "Fast-feet, ball control, first touch, 1v1/2v2 duels, small-sided games"}</p></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Espacios limitados para atencion personalizada" : "Limited spots for personalized attention"}</p></div>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fly-right" delay={100}>
              <div className="bg-white rounded-2xl border-2 border-[#87CEEB] overflow-hidden h-full shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="bg-[#87CEEB] text-black px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    <h3 className="font-bold font-['Kanit']">{lang === "es" ? "Martes y Jueves" : "Tuesdays & Thursdays"}</h3>
                  </div>
                  <span className="text-xl font-bold font-['Kanit']">$50</span>
                </div>
                <div className="p-5 space-y-2.5">
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{lang === "es" ? "Edades 8 - 10 anos | por 8 sesiones" : "Ages 8 - 10 years old | for 8 sessions"}</p>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Min 8 / Max 15 jugadores" : "Min 8 / Max 15 players"}</p></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Pies rapidos, control, primer toque, duelos 1v1/2v2, juegos reducidos" : "Fast-feet, ball control, first touch, 1v1/2v2 duels, small-sided games"}</p></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Espacios limitados para atencion personalizada" : "Limited spots for personalized attention"}</p></div>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* ── Section 3: Small Group + Private Training (side by side) ── */}
          <AnimatedSection animation="fly-up" delay={200}>
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 font-['Kanit'] mb-1">{lang === "es" ? "Entrenamiento Adicional" : "Additional Training Options"}</h3>
              <p className="text-sm text-gray-500">{lang === "es" ? "Sesiones de grupo pequeno y privadas disponibles" : "Small group and private sessions available"}</p>
            </div>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-5 mb-10" data-testid="group-private-section">
            <AnimatedSection animation="fly-left" delay={300}>
              <div className="bg-white rounded-2xl border-2 border-emerald-400 overflow-hidden h-full shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="bg-emerald-400 text-black px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    <h3 className="font-bold font-['Kanit']">{lang === "es" ? "Grupo Pequeno" : "Small Group Training"}</h3>
                  </div>
                  <span className="text-xl font-bold font-['Kanit']">$20</span>
                </div>
                <div className="p-5 space-y-2.5">
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{lang === "es" ? "por jugador" : "/ player"}</p>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "2-4 jugadores por grupo" : "2-4 players per group"}</p></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Enfoque en quimica y dinamica tecnica" : "Focus on chemistry and technical dynamics"}</p></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Espacios limitados para atencion personalizada" : "Limited spots for personalized attention"}</p></div>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fly-right" delay={400}>
              <div className="bg-white rounded-2xl border-2 border-amber-400 overflow-hidden h-full shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="bg-amber-400 text-black px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    <h3 className="font-bold font-['Kanit']">{lang === "es" ? "Entrenamiento Privado" : "Private Training"}</h3>
                  </div>
                  <span className="text-xl font-bold font-['Kanit']">$35</span>
                </div>
                <div className="p-5 space-y-2.5">
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{lang === "es" ? "por sesion" : "/ session"}</p>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Correccion de habilidades 1-a-1" : "1-on-1 personalized skill correction"}</p></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Desarrollo de elite personalizado" : "Elite development tailored to your athlete"}</p></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Horario flexible" : "Flexible scheduling"}</p></div>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* ── Video Analytics Add-on (small strip) ── */}
          <AnimatedSection animation="fly-up" delay={300}>
            <div className="bg-violet-50 border border-violet-200 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-400 rounded-lg flex items-center justify-center">
                  <Crosshair className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="font-bold text-gray-800 font-['Kanit']">{lang === "es" ? "Video Analitico" : "Video Analytics Add-on"}</span>
                  <p className="text-sm text-gray-500">{lang === "es" ? "Revisiones profesionales de rendimiento usando video." : "Professional performance reviews using video to analyze technique."}</p>
                </div>
              </div>
              <span className="text-xl font-bold text-violet-600 font-['Kanit'] whitespace-nowrap">+$15 <span className="text-sm font-normal text-gray-400">{lang === "es" ? "/ sesion" : "/ session"}</span></span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="fly-left">
              <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">{t.coaching.programBadge}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Kanit']">{t.coaching.programTitle}</h2>
              <p className="text-gray-700 mb-4">{t.coaching.programDesc}</p>
              <p className="text-[#87CEEB] font-semibold">{t.coaching.programCta}</p>
            </AnimatedSection>
            <AnimatedSection animation="fly-right" delay={200}>
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center"><Calendar className="w-6 h-6 text-black" /></div>
                  <div><p className="font-bold text-black font-['Kanit']">{t.coaching.schedule}</p><p className="text-gray-600">{t.coaching.scheduleDays}</p><p className="text-sm text-gray-400">{t.coaching.scheduleNote}</p></div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center"><Clock className="w-6 h-6 text-black" /></div>
                  <div><p className="font-bold text-black font-['Kanit']">{t.coaching.time}</p><p className="text-gray-600">{t.coaching.timeVal}</p><p className="text-sm text-gray-400"><AnimatedCounter end={1} /> {t.coaching.timeNote}</p></div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center"><Users className="w-6 h-6 text-black" /></div>
                  <div><p className="font-bold text-black font-['Kanit']">{t.coaching.agesLabel}</p><p className="text-gray-600"><AnimatedCounter end={8} /> – <AnimatedCounter end={10} /> {t.coaching.agesVal}</p><p className="text-sm text-gray-400">{t.coaching.agesNote}</p></div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center"><DollarSign className="w-6 h-6 text-black" /></div>
                  <div><p className="font-bold text-black font-['Kanit']">{t.coaching.investment}</p><p className="text-gray-600">$<AnimatedCounter end={50} /> / <AnimatedCounter end={8} /> sessions</p><p className="text-sm text-gray-400">{t.coaching.lessText} $<AnimatedCounter end={7} />/class</p></div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-12">
            <h2 className="text-3xl font-bold text-black mb-4 font-['Kanit']">{t.coaching.focusTitle}</h2>
          </AnimatedSection>
          <div className="flex flex-wrap justify-center gap-4">
            {t.coaching.focusAreas.map((area, index) => (
              <div key={area} className="animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                <span className="inline-flex items-center gap-2 bg-[#F0F9FF] px-6 py-3 rounded-full text-black font-semibold hover:bg-[#87CEEB] transition-colors cursor-default">
                  <CheckCircle className="w-5 h-5 text-[#87CEEB]" />{area}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivateCoachingPage;
